import React from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';

import Badge from 'components/Badge';
import Button from 'components/Button';
import Message from 'components/Message';
import PageTurner from 'components/PageTurner/PageTurner';
import { Table } from 'components/Table';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { formatOrderId } from 'helpers/sku';

import { formatOrderItemsSummary } from '../Orders/SkuOrderDashboard.helpers';
import { LIFECYCLE_STATUS } from '../shared/SkuEnumHelpers';

import { ORDERS_REPORT_PAGE_SIZE } from './SkuOrderReports.hooks';
import messages from './SkuOrderReports.messages';
import VenueLabel from './SkuReportsVenueLabel';

const { element } = bem('sku-order-reports');

const EMPTY_VALUE = '—';

const COLUMNS = [
  { key: 'orderId', label: messages.orderIdColumn },
  { key: 'location', label: messages.locationColumn },
  { key: 'items', label: messages.itemsColumn },
  { key: 'pickUpTime', label: messages.pickUpTimeColumn },
  { key: 'customer', label: messages.customerColumn },
  { key: 'status', label: messages.statusColumn },
  { key: 'earnings', label: messages.earningsColumn, align: 'right' },
];

const MOBILE_COLUMNS = [
  { key: 'order', label: messages.mobileOrderColumn },
  { key: 'earnings', label: messages.earningsColumn, align: 'right' },
];

const STATUS_BADGES = {
  [LIFECYCLE_STATUS.FULFILLED]: { message: messages.completedStatus, props: { solid: true, info: true } },
  [LIFECYCLE_STATUS.CANCELLED]: { message: messages.cancelledStatus, props: { solid: true, warn: true } },
  [LIFECYCLE_STATUS.ACTIVE]: { message: messages.preparingStatus, props: { disabled: true } },
};

const moneyShape = PropTypes.shape({ amount: PropTypes.number, currencyCode: PropTypes.string });

const orderShape = PropTypes.shape({
  core: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    requestedPickupTime: PropTypes.string,
  }).isRequired,
  orderItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userName: PropTypes.string,
  venueName: PropTypes.string,
  venueSubtitle: PropTypes.string,
  venueTimezone: PropTypes.string,
  lifecycleStatus: PropTypes.string,
  payableAmount: moneyShape,
});

const StatusBadge = ({ lifecycleStatus }) => {
  const badge = STATUS_BADGES[lifecycleStatus];
  if (!badge) return EMPTY_VALUE;
  return <Badge size="xs" className={element('status')} message={badge.message} {...badge.props} />;
};

StatusBadge.propTypes = { lifecycleStatus: PropTypes.string };

const EarningsCell = ({ earnings }) => (
  <td className={element('earnings')}>
    <span className={element('earnings-cell')}>
      {earnings ? <Message format="currency" value={earnings.amount} currency={earnings.currencyCode} /> : EMPTY_VALUE}
      <Isvg className={element('chevron')} src={ICONS.chevronRightThin} aria-hidden="true" />
    </span>
  </td>
);

EarningsCell.propTypes = { earnings: moneyShape };

const PickUpTime = ({ isoDateTime, timeZone }) => {
  const intl = useIntl();
  if (!isoDateTime) return EMPTY_VALUE;
  return intl.formatDate(new Date(isoDateTime), {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    timeZone: timeZone || undefined,
  });
};

PickUpTime.propTypes = { isoDateTime: PropTypes.string, timeZone: PropTypes.string };

const rowInteractionProps = (order, onRowClick) => ({
  className: element('row'),
  role: 'button',
  tabIndex: 0,
  onClick: () => onRowClick(order),
  onKeyDown: event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(order);
    }
  },
});

const OrderRow = ({ order, onRowClick, showLocationColumn }) => {
  const items = formatOrderItemsSummary(order.orderItems);

  return (
    <tr {...rowInteractionProps(order, onRowClick)}>
      <td>{formatOrderId(order.core.orderId)}</td>
      {showLocationColumn && (
        <td>
          <VenueLabel name={order.venueName} subtitle={order.venueSubtitle} />
        </td>
      )}
      <td className={element('items')} title={items || undefined}>
        {items || EMPTY_VALUE}
      </td>
      <td>
        <PickUpTime isoDateTime={order.core.requestedPickupTime} timeZone={order.venueTimezone} />
      </td>
      <td>{order.userName || EMPTY_VALUE}</td>
      <td>
        <StatusBadge lifecycleStatus={order.lifecycleStatus} />
      </td>
      <EarningsCell earnings={order.payableAmount} />
    </tr>
  );
};

OrderRow.propTypes = {
  order: orderShape.isRequired,
  onRowClick: PropTypes.func.isRequired,
  showLocationColumn: PropTypes.bool,
};

const MobileOrderRow = ({ order, onRowClick }) => (
  <tr {...rowInteractionProps(order, onRowClick)}>
    <td>
      <span className={element('mobile-order-id')}>{formatOrderId(order.core.orderId)}</span>
      <span className={element('mobile-pick-up-time')}>
        <PickUpTime isoDateTime={order.core.requestedPickupTime} timeZone={order.venueTimezone} />
      </span>
    </td>
    <EarningsCell earnings={order.payableAmount} />
  </tr>
);

MobileOrderRow.propTypes = OrderRow.propTypes;

const EmptyState = () => (
  <tr>
    <td colSpan={100}>
      <div className={element('empty')}>
        <Isvg className={element('empty-icon')} src={ICONS.inbox} />
        <div className={element('empty-title')}>
          <Message message={messages.emptyTitle} />
        </div>
        <div className={element('empty-body')}>
          <Message message={messages.emptyBody} />
        </div>
      </div>
    </td>
  </tr>
);

const ErrorState = ({ onRetry }) => (
  <tr>
    <td colSpan={100}>
      <div className={element('empty')}>
        <div className={element('empty-title')}>
          <Message message={messages.errorTitle} />
        </div>
        <div className={element('empty-body')}>
          <Message message={messages.errorBody} />
        </div>
        <Button styled="secondary" onClick={onRetry}>
          <Message message={messages.retry} />
        </Button>
      </div>
    </td>
  </tr>
);

ErrorState.propTypes = { onRetry: PropTypes.func.isRequired };

const SkuOrderReportsTable = ({
  orders,
  totalCount,
  showLocationColumn = true,
  isMobile,
  isLoading,
  isError,
  onRetry,
  page,
  nextPageCursor,
  onChangePage,
  onRowClick,
}) => {
  const columns = (isMobile ? MOBILE_COLUMNS : COLUMNS).filter(
    column => showLocationColumn || column.key !== 'location'
  );
  const Row = isMobile ? MobileOrderRow : OrderRow;
  const startNumber = orders.length ? (page - 1) * ORDERS_REPORT_PAGE_SIZE + 1 : 0;
  const endNumber = orders.length ? startNumber + orders.length - 1 : 0;

  const header = (
    <tr>
      {columns.map(column => (
        <th className={column.align === 'right' ? element('th-right') : undefined} key={column.key}>
          <Message message={column.label} />
        </th>
      ))}
    </tr>
  );

  const renderBody = () => {
    if (isError) return [<ErrorState key="error" onRetry={onRetry} />];
    if (orders.length === 0) return [<EmptyState key="empty" />];
    return orders.map(order => (
      <Row key={order.core.orderId} order={order} onRowClick={onRowClick} showLocationColumn={showLocationColumn} />
    ));
  };

  return (
    <>
      <Table className={element('table', { 'no-location': !showLocationColumn })} header={header} isLoading={isLoading}>
        {renderBody()}
      </Table>
      {!isError && (
        <PageTurner
          className={element('page-turner')}
          count={totalCount}
          startNumber={startNumber}
          endNumber={endNumber}
          isLoading={isLoading}
          isOnFirstPage={page === 1}
          isOnLastPage={!nextPageCursor}
          viewNextPage={() => onChangePage(page + 1)}
          viewPrevPage={() => onChangePage(page - 1)}
        />
      )}
    </>
  );
};

SkuOrderReportsTable.propTypes = {
  orders: PropTypes.arrayOf(orderShape).isRequired,
  totalCount: PropTypes.number.isRequired,
  showLocationColumn: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  nextPageCursor: PropTypes.string,
  onChangePage: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
};

export default SkuOrderReportsTable;
